import { useTranslation } from "react-i18next";
import { Outlet, useMatch, useParams } from "react-router";
import { errorCodeOf } from "../../api/fetcher";
import { useBook } from "../../api/hooks";
import { Button } from "../../components/control/Button";
import { Banner } from "../../components/feedback/Banner";
import { ErrorState } from "../../components/feedback/ErrorState";
import { Skeleton } from "../../components/feedback/Skeleton";
import { AppShell } from "../../components/layout/AppShell";
import { TopNav } from "../../components/layout/TopNav";
import { BookStatusBanner } from "./BookStatusBanner";
import { BookWorkspace } from "./BookWorkspace";
import { BookProvider } from "./bookContext";
import { MetadataConfirmation } from "./MetadataConfirmation";

/**
 * The frame of every /books/:id screen. It owns the one poll (GET
 * /api/books/:id every 2 s while work is active, off when nothing is) and
 * hands the payload down through context, so however many panels a screen
 * shows, the tab makes one request per tick.
 *
 * Review is the one screen that takes the frame's whole body: it puts a Chapter
 * rail against the left edge and scrolls the alignment inside a fixed window
 * rather than scrolling the page, which is what `AppShell layout="flush"`
 * exists for. Every other screen keeps the padded page layout.
 */
export function BookLayout() {
  const { t } = useTranslation(["common", "nav", "components"]);
  const { id = "" } = useParams();
  const query = useBook(id);
  // Both matches are read unconditionally and above every early return: `||`
  // would short-circuit the second hook away on a Review route, and an early
  // return would drop both, either of which changes the hook order.
  const reviewChapter = useMatch("/books/:id/review/*");
  const reviewIndex = useMatch("/books/:id/review");
  const flush = reviewChapter !== null || reviewIndex !== null;
  const book = query.data;
  const code = query.isError ? errorCodeOf(query.error) : null;
  // A poll that comes back 404 means the Book is gone, so the cached copy is
  // not a last known state, it is a lie; anything else (a dropped connection,
  // a 500) leaves the page standing and says so above it.
  const gone = code === "NOT_FOUND";
  // Built here rather than in the JSX: the literal is an error code, and the
  // no-literal-string rule reads every literal inside a prop as text.
  const shownCode = code ?? "INTERNAL";

  if (query.isPending) {
    return (
      <AppShell nav={<TopNav />}>
        <div className="min-w-0 flex-1">
          <Skeleton variant="text" lines={4} />
        </div>
      </AppShell>
    );
  }
  if (!book || gone) {
    return (
      <AppShell nav={<TopNav />}>
        <div className="min-w-0 flex-1">
          <ErrorState code={shownCode} onRetry={() => void query.refetch()} busy={query.isFetching}>
            {t("common:bookLoadError")}
          </ErrorState>
        </div>
      </AppShell>
    );
  }

  return (
    <BookProvider value={{ book, isFetching: query.isFetching }}>
      <MetadataConfirmation key={`metadata-${book.id}`} />
      <BookWorkspace
        key={`workspace-${book.id}`}
        flush={flush}
        banner={
          <>
            {code ? (
              <Banner
                tone="warn"
                action={
                  <Button
                    variant="secondary"
                    size="sm"
                    busy={query.isFetching}
                    onClick={() => void query.refetch()}
                  >
                    {t("components:error.retry")}
                  </Button>
                }
              >
                {t("common:bookStaleError")}
              </Banner>
            ) : null}
            <BookStatusBanner />
          </>
        }
      >
        {flush ? (
          <Outlet />
        ) : (
          <div className="min-w-0 flex-1">
            <Outlet />
          </div>
        )}
      </BookWorkspace>
    </BookProvider>
  );
}
